import type { FCWithChildren, ReferencedData } from '../types'
import { type ReactNode, useCallback, useMemo } from 'react'
import { isObject, mean } from 'lodash'
import { Badge } from './Badge'
import { useSpeciesDetail } from '@/context/speciesDetail'
import { Popover } from '@base-ui/react/popover'

type ReferencedDataFields<T extends keyof ReferencedData> =
  keyof ReferencedData[T][number]

type CommonReferencedDataFields =
  keyof ReferencedData[keyof ReferencedData][number]

type DataTableConfig<T extends keyof ReferencedData> = {
  exclude?: Array<ReferencedDataFields<T>>
  labels?: Partial<Record<ReferencedDataFields<T>, string>>
  nested?: Partial<Record<ReferencedDataFields<T>, string[]>>
  order?: Array<ReferencedDataFields<T>>
}

const alwaysExclude: CommonReferencedDataFields[] = [
  'id',
  'species_id',
  'reference_id',
] as const

const dataCardTables: { [T in keyof ReferencedData]?: DataTableConfig<T> } = {
  germination: {
    labels: {
      percent_germ: '% Germination',
      presow_treatment: 'Treatment',
      light_hours: 'Light Hours',
    },
    order: [
      'percent_germ',
      'sample_size',
      'medium',
      'light_hours',
      'temperature',
      'days',
      'presow_treatment',
      'provenance_country',
      'provenance_detail',
    ],
  },
  dispersal: {
    order: [
      'dispersal_agents',
      'animal_group',
      'animal_species',
      'method',
      'provenance_country',
      'provenance_detail',
      'notes',
    ],
    labels: {
      dispersal_agents: 'Dispersal agent',
    },
    nested: {
      dispersal_agents: ['description'],
    },
  },
  seed_weights: {
    labels: {
      thousandseedweight: '1000 seed weight (g)',
    },
    order: [
      'thousandseedweight',
      'material_weighed',
      'provenance_country',
      'provenance_detail',
      'notes',
    ],
    nested: {
      material_weighed: ['description'],
    },
  },
  oil_content: {
    exclude: ['material_id'],
    order: [
      'oil_content',
      'moisture_status',
      'material_weighed',
      'provenance_country',
      'provenance_detail',
      'notes',
    ],
    nested: {
      material_weighed: ['description'],
    },
  },
  protein_content: {
    exclude: ['material_id'],
    order: [
      'protein_content',
      'moisture_status',
      'material_weighed',
      'provenance_country',
      'provenance_detail',
      'notes',
    ],
    nested: {
      material_weighed: ['description'],
    },
  },
}

export const makeTitleCase = (text: string) =>
  text
    .toLocaleLowerCase()
    .split(/[_ ]/g)
    .map((word) => `${word[0].toLocaleUpperCase()}${word.substring(1)}`)
    .join(' ')

export const DataCard: FCWithChildren<{
  title: string
  headings: string[]
  rows: Array<Array<string | number>>
  onHoverRow?: (data: number) => void
}> = ({ title, headings, rows, onHoverRow, children }) => {
  const { hoveredRef } = useSpeciesDetail()

  return (
    <div className="sm:w-100 mt-2 block rounded-lg border border-teal-200 bg-white p-6 ">
      <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900">
        {title}
      </h5>
      {children}
      <div className="overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-left text-sm text-gray-500">
          <thead className="bg-gray-50 text-xs uppercase text-gray-700">
            <tr>
              {headings.map((field) => (
                <th key={field} scope="col" className="px-6 py-3">
                  {field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((dataRow, i) => (
              <Popover.Root key={`row-${i}`}>
                <Popover.Trigger
                  openOnHover
                  render={
                    <tr
                      className="border-b odd:bg-white even:bg-gray-50"
                      onMouseEnter={() => onHoverRow?.(i)}
                    />
                  }
                >
                  {dataRow.map((field, ii) => (
                    <th
                      key={`h-${i}-${ii}`}
                      scope="row"
                      className="whitespace-nowrap px-4 py-2 font-medium text-gray-900"
                    >
                      {field}
                    </th>
                  ))}
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Positioner align="end" side="top">
                    <Popover.Popup className="bg-white border border-serblue-light p-2 rounded-lg shadow-md">
                      <Popover.Title className="max-w-96 text-xs">
                        {hoveredRef?.description}
                      </Popover.Title>
                    </Popover.Popup>
                  </Popover.Positioner>
                </Popover.Portal>
              </Popover.Root>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

type FieldList<T extends keyof ReferencedData> = Array<ReferencedDataFields<T>>

type SpeciesDataCardProps<T extends keyof ReferencedData> = {
  data: ReferencedData[T]
  title: T
  children?: ReactNode
}

type DataRow<T extends keyof ReferencedData> = ReferencedData[T][number]

const orderFn =
  <D extends keyof ReferencedData>(dataGroup: D) =>
  (a: ReferencedDataFields<D>, b: ReferencedDataFields<D>) => {
    const order = dataCardTables[dataGroup]?.order

    if (order && order.includes(a) && order.includes(b))
      return order.indexOf(a) - order.indexOf(b)

    return 1
  }

// helper method thanks to https://fettblog.eu/typescript-array-includes/
function includes<T extends U, U>(coll: readonly T[], el: U): el is T {
  return coll.includes(el as T)
}

export const SpeciesDataCard = <T extends keyof ReferencedData>({
  data,
  title,
  children,
}: SpeciesDataCardProps<T>) => {
  const { handleHoverData, hoveredRef } = useSpeciesDetail()

  const handleHoverRow = useCallback(
    (rowIndex: number) => {
      const dataRow = data[rowIndex]
      if (!dataRow) return
      const refId = dataRow.reference_id
      if (!refId) return
      handleHoverData(refId)
    },
    [data, handleHoverData],
  )

  const config = dataCardTables[title]
  const dataKeys = Object.keys(data[0])
  const excludedFields = [
    ...alwaysExclude,
    ...(config?.exclude ?? []),
  ] as string[]
  const fieldList = dataKeys.filter(
    (key) => !includes(excludedFields, key),
  ) as FieldList<T>

  if (config?.nested) {
    fieldList.push(...(Object.keys(config.nested) as FieldList<T>))
  }

  const fields = fieldList.reduce<
    Partial<Record<keyof ReferencedData[T][0], string>>
  >((result, field) => {
    // create labels for fields
    result[field] = makeTitleCase(field as string)

    if (config?.labels && config?.labels[field]) {
      result[field] = config.labels[field]
    }
    return result
  }, {})
  const headings = (Object.keys(fields) as FieldList<T>)
    .sort(orderFn(title))
    .map((field) => fields[field] ?? '')

  const rows: Array<Array<string | number>> = useMemo(
    () =>
      data.map((dataRow: DataRow<T>) =>
        (Object.keys(fields) as Array<keyof DataRow<T>>)
          .sort(orderFn(title))
          .map((field) => {
            const rawData = dataRow[field]
            if (!rawData) return ''

            if (isObject(rawData)) {
              // get nested data if it exists for field
              if (config?.nested?.[field]) {
                const path = config.nested[field]
                let extractedData = rawData
                path?.forEach((key) => {
                  // @ts-expect-error hard to type path walked through object
                  extractedData = extractedData[key]
                })
                return extractedData as string | number
              }
            }
            return rawData as string | number
          }),
      ),
    [data, fields, title, config?.nested],
  )

  return (
    <DataCard
      title={makeTitleCase(title)}
      headings={headings}
      rows={rows}
      onHoverRow={handleHoverRow}
    >
      {children}
    </DataCard>
  )
}

export const AverageContentDataCard = <
  T extends keyof Omit<ReferencedData, 'germination' | 'dispersal'>,
  D extends ReferencedData[T],
>({
  data,
  title,
}: {
  title: T
  data: D
}) => {
  const valueKey = title === 'seed_weights' ? 'thousandseedweight' : title
  const suffix = title === 'seed_weights' ? 'g' : '%'
  const averageText = title === 'seed_weights' ? 'Seed weight' : title

  const average = mean(data.map((val) => val[valueKey as keyof typeof val]))

  return (
    <SpeciesDataCard {...{ data, title }}>
      <div className="mb-2">
        <Badge
          title={`Mean ${makeTitleCase(averageText)} ${average.toFixed(
            2,
          )}${suffix}`}
        />
      </div>
    </SpeciesDataCard>
  )
}
